import React from 'react'
import { StyleSheet } from 'react-native'

import { useNavigation } from '@zet/common/hooks/useNavigation'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import {
  Button,
  colors,
  Icon,
  Pressable,
  Stack,
  XStack,
  YStack,
  colors as zetColors,
} from '@zet/design-system'
import { B2, B3, B4, B5, BTN, SH3 } from '@zet/design-system/typography'
import { APP_NAME } from '@zet/end-points'
import { LeadDetails } from '@zet/feature-leads/api/leadDetails/types'
import { useUserContext } from '@zet/feature-user'
import ZetCoinsFilled from '@zet/homepage-widgets/src/assets/svg/zetCoinsFilled.svg'
import { i18n } from '@zet/localization'
import {
  LeadStatus,
  PaymentCardStatus,
  PaymentStatus,
  PayoutType,
} from '@zet/utils/Constants'
import _ from 'lodash'
import moment from 'moment'

import BonusBanner from './bonusBanner'
import CoinsConditionCard from './coinsConditionCard'

type PaymentCardProps = {
  item?: LeadDetails
  brandName?: string
  hideBottomView?: boolean
  setOpenBottomSheet: React.Dispatch<React.SetStateAction<boolean>>
}

const PaymentCard = ({
  item,
  brandName,
  hideBottomView,
  setOpenBottomSheet,
}: PaymentCardProps) => {
  const { navigate } = useNavigation()
  function openBsheet() {
    setOpenBottomSheet(true)
  }
  const payment = item?.partnerTransaction
  const coins = item?.coinTransactions || []
  const status = payment?.[0]?.status
  const payoutDate = payment?.[0]?.payoutDate
  const leadStatus = item?.status
  const partnerId = item?.partnerId
  const coinsLimit = item?.coinsData
  const { data: user } = useUserContext()

  const onCoinsLimitKnowMore = () => {
    navigate('LoyaltyDetailedFaqPage', {
      faqKey: 'zetCoins',
      faqTitle: i18n.t('coinsLedger.zetCoin'),
    })
  }

  const totalCommission = () => {
    return leadStatus === LeadStatus.PARTIALLY_FULLFILLED ? (
      item?.totalCommission ? (
        <Pressable onPress={openBsheet}>
          <XStack className=" border-primary-30 mx-3 mb-2 items-center justify-center rounded border py-2">
            <BTN className="text-primary-100">
              {i18n.t('payment.totalCommission', {
                amount: item?.totalCommission,
              })}
            </BTN>
            <Icon type={'ArrowRight'} color={zetColors.primary[100]} />
          </XStack>
        </Pressable>
      ) : null
    ) : null
  }

  const customerAccountInfo = () => {
    return leadStatus === LeadStatus.PARTIALLY_FULLFILLED ? (
      item?.commissionLevels?.[0]?.status ? (
        <SH3 className="text-grays-80">
          {i18n.t('payment.customerAccountInfo', {
            account: item?.commissionLevels?.[0]?.status,
          })}
        </SH3>
      ) : null
    ) : null
  }

  const payoutCards = (payment: any) => {
    if (payment?.status === PaymentStatus.PENDING) {
      return (
        <Stack className="bg-secondary-10 mx-3 my-2 rounded">
          <BonusBanner data={item} payment={payment} />
          <YStack className="py-4/2 px-2">
            <B4 className="text-grays-70">
              {i18n.t('payment.paymentDueOn', {
                date: moment.utc(payment?.payoutDate).format('DD-MMM-YYYY'),
              })}
            </B4>
            {customerAccountInfo()}
            <B3 className="text-primary-200">
              {item?.isKycDone
                ? i18n.t('payment.paymentPending', {
                    amount: payment?.commission,
                    date: moment.utc(payment?.payoutDate).format('DD-MMM-YYYY'),
                  })
                : i18n.t('payment.paymentPendingCompleteKyc', {
                    amount: payment?.commission,
                  })}
            </B3>
          </YStack>
        </Stack>
      )
    } else if (payment?.status === PaymentStatus.PAID) {
      return (
        <Stack className="bg-success-8 mx-3 my-2 rounded">
          <BonusBanner data={item} payment={payment} />
          {payment?.coins && (
            <B5
              className="mx-2 -mb-2 mt-2 self-start rounded border bg-white p-2"
              style={styles.coinsBorder}
            >
              <Stack>
                <ZetCoinsFilled className="top-1 mr-1" height={19} width={19} />
              </Stack>
              {i18n.t('payoutBreakoutLoyalty.zetCoins')}
            </B5>
          )}
          <YStack className="py-4/2 px-2">
            <B4 className="text-grays-70">
              {i18n.t('payment.paidOn', {
                date: moment.utc(payment?.payoutDate).format('DD-MMM-YYYY'),
              })}
            </B4>
            {customerAccountInfo()}
            {payment?.commission && (
              <B3 className="text-success-150">
                {i18n.t('payment.paymentSuccessfull', {
                  amount: payment?.commission,
                })}
              </B3>
            )}
            {Boolean(payment?.coins) && (
              <B3 className="text-success-150">
                {i18n.t('payoutBreakoutLoyalty.transfered', {
                  coin: payment?.coins,
                })}
              </B3>
            )}
          </YStack>
        </Stack>
      )
    } else if (payment?.status === PaymentStatus.FAILED) {
      return (
        <Stack className="bg-warning-10 mx-3 mb-2 rounded">
          <BonusBanner data={item} payment={payment} />
          <YStack className="py-4/2 px-2">
            <B4 className="text-grays-70">
              {i18n.t('payment.paymentFailed', {
                date: moment.utc(payment?.payoutDate).format('DD-MMM-YYYY'),
              })}
            </B4>
            {customerAccountInfo()}
            <B3 className="text-error-200">
              {i18n.t('payment.paymentFailedDescription', {
                amount: payment?.commission,
              })}
            </B3>
          </YStack>
        </Stack>
      )
    } else if (payment?.status === PaymentStatus.REJECTED) {
      return (
        <Stack className="mb-4/2 bg-error-10 mx-3 rounded">
          <BonusBanner data={item} payment={payment} />
          <YStack className="py-4/2 px-2">
            <B4 className="text-grays-70">
              {i18n.t('payment.paymentRejected', {
                amount: payment?.commission,
              })}
            </B4>
            {customerAccountInfo()}
            <B3 className="text-error-200">
              {payment?.failureReason
                ? payment?.failureReason
                : i18n.t('payment.rejectionReason')}
            </B3>
          </YStack>
        </Stack>
      )
    } else if (payment?.status === PaymentStatus.LOCKED) {
      return (
        <Stack className="mb-4/2 bg-grays-5 mx-3 rounded">
          <BonusBanner data={item} payment={payment} />
          <YStack className="py-4/2 px-2">
            <B4 className="text-grays-70">
              {i18n.t('payment.paymentLocked', {
                amount: payment?.commission,
              })}
            </B4>
            {customerAccountInfo()}
            {payment?.payoutType === PayoutType.DEMO ? (
              <B3 className="text-grays-60">
                {i18n.t('loyaltyDetails.demoSale')}
              </B3>
            ) : (
              <B3 className="text-grays-60">
                {i18n.t('loyaltyDetails.becomeExpert')}
              </B3>
            )}
          </YStack>
        </Stack>
      )
    } else if (
      !PaymentCardStatus.includes(payment?.status) &&
      (leadStatus === LeadStatus.PARTIALLY_FULLFILLED ||
        leadStatus === LeadStatus.FULLFILLED)
    ) {
      return (
        <Stack className="mb-4/2 bg-error-10 mx-3 rounded">
          <YStack className="py-4/2 px-2">
            <B3 className="text-grays-70">
              {i18n.t('payment.customerBackInfo')}
            </B3>
          </YStack>
        </Stack>
      )
    }
  }

  const commissionLevels = () => {
    return leadStatus === LeadStatus.PARTIALLY_FULLFILLED ? (
      item?.commissionLevels?.[0]?.description ? (
        <Stack className="bg-grays-20 px-4/2 border-grays-40 mx-3 mb-2 items-start rounded border py-2">
          {item?.commissionLevels?.[0]?.isRecurring ? (
            <B2>
              {i18n.t('payment.commissionLevels')}
              {item?.commissionLevels?.[0]?.description}
            </B2>
          ) : (
            <B2>{item?.commissionLevels?.[0]?.description} </B2>
          )}
        </Stack>
      ) : null
    ) : null
  }

  if (
    APP_NAME === MY_TEAMS_ADVISOR &&
    leadStatus === LeadStatus.PARTIALLY_FULLFILLED
  ) {
    return (
      <Stack className="border-secondary-100 bg-neutral-white100 mt-2 rounded-sm border-l-4 pt-2">
        {commissionLevels()}
      </Stack>
    )
  }

  if (
    APP_NAME === MY_TEAMS_ADVISOR ||
    hideBottomView
    // &&
    // (leadStatus === LeadStatus.FULLFILLED || leadStatus === LeadStatus.REJECTED)
  ) {
    return <></>
  }

  return (
    <Stack>
      {status === PaymentStatus.PENDING ? (
        <Stack
          className="bg-neutral-white100 mt-2 rounded-sm border-l-4"
          style={styles.sideBorder}
        >
          <XStack className="py-4/2 px-3">
            <Stack className="bg-success-150 absolute left-[30] h-4 w-1" />
            <Stack className="bg-success-150 absolute left-3 top-3 items-center rounded-lg">
              <Icon
                type={'RupeeCircleOutlined'}
                size={20}
                color={zetColors.neutral['white100']}
                className="m-1.5"
              />
            </Stack>
            <Stack className="left-6 mt-2 justify-center">
              <SH3 className="  text-grays-80">{i18n.t('payment.payment')}</SH3>
            </Stack>
          </XStack>
          {commissionLevels()}
          {totalCommission()}
          {payment?.concat(coins)?.map((payment) => payoutCards(payment))}
          {!_.isEmpty(coinsLimit) ? (
            <CoinsConditionCard
              onCoinsLimitKnowMore={onCoinsLimitKnowMore}
              coinsLimit={coinsLimit}
            />
          ) : (
            <></>
          )}

          {item?.isKycDone ? null : (
            <Stack className="mb-4/2 mx-3">
              <Button
                fullWidth
                size={'md'}
                className={
                  'bg-primary-100 items-center justify-center px-3 py-2'
                }
                onPress={() => navigate('AddBankAccount')}
                eventName="click_action"
                eventPayload={{
                  item_name: 'lead_page_complete_kyc',
                  lead_id: item?.id,
                  lead_status: leadStatus,
                  user_id: user?.id,
                  customer_id: item?.customerId,
                  partner_id: partnerId,
                  brand_name: brandName,
                }}
              >
                <BTN className="text-neutral-white100 text-center">
                  {i18n.t('payment.completeKyc')}
                </BTN>
              </Button>
            </Stack>
          )}
        </Stack>
      ) : null}

      {status === PaymentStatus.PAID ? (
        <Stack
          className="bg-neutral-white100 mt-2 rounded-sm border-l-4"
          style={styles.sideBorder}
        >
          <XStack className="py-4/2 px-3">
            <Stack className="bg-success-150 absolute left-[30] h-4 w-1" />
            <Stack className="bg-success-150 absolute left-3 top-3 items-center rounded-lg">
              <Icon
                type={'RupeeCircleOutlined'}
                size={20}
                color={zetColors.neutral.white100}
                className="m-1.5"
              />
            </Stack>
            <Stack className="left-6 mt-2 justify-center">
              <SH3 className="  text-grays-80">{i18n.t('payment.payment')}</SH3>
            </Stack>
          </XStack>
          {commissionLevels()}
          {totalCommission()}
          {payment?.concat(coins)?.map((payment) => payoutCards(payment))}
          {!_.isEmpty(coinsLimit) ? (
            <CoinsConditionCard
              onCoinsLimitKnowMore={onCoinsLimitKnowMore}
              coinsLimit={coinsLimit}
            />
          ) : (
            <></>
          )}
        </Stack>
      ) : null}

      {status === PaymentStatus.FAILED ? (
        <Stack
          className="bg-neutral-white100 mt-2 rounded-sm border-l-4 "
          style={styles.sideBorder}
        >
          <XStack className="py-4/2 px-3">
            <Stack className="bg-success-150 absolute left-[30] h-4 w-1" />
            <Stack className="bg-success-150 absolute left-3 top-3 items-center rounded-lg">
              <Icon
                type={'RupeeCircleOutlined'}
                size={20}
                color={zetColors.neutral['white100']}
                className="m-1.5"
              />
            </Stack>
            <Stack className="left-6 mt-2 justify-center">
              <SH3 className="  text-grays-80">{i18n.t('payment.payment')}</SH3>
            </Stack>
          </XStack>
          {commissionLevels()}
          {totalCommission()}
          {payment?.concat(coins)?.map((payment) => payoutCards(payment))}
          {!_.isEmpty(coinsLimit) ? (
            <CoinsConditionCard
              onCoinsLimitKnowMore={onCoinsLimitKnowMore}
              coinsLimit={coinsLimit}
            />
          ) : (
            <></>
          )}

          {item?.isKycDone ? (
            <Stack className="mb-4/2 mx-3">
              <Button
                fullWidth
                size={'md'}
                className={
                  'bg-primary-100 items-center justify-center px-3 py-2 '
                }
                onPress={() => navigate('PaymentModes')}
                eventName="click_action"
                eventPayload={{
                  item_name: 'lead_page_check_account_details',
                  lead_id: item?.id,
                  lead_status: leadStatus,
                  user_id: user?.id,
                  customer_id: item?.customerId,
                  partner_id: partnerId,
                  brand_name: brandName,
                }}
              >
                <BTN className="text-neutral-white100 text-center">
                  {i18n.t('payment.checkDetails')}
                </BTN>
              </Button>
            </Stack>
          ) : null}
        </Stack>
      ) : null}

      {status === PaymentStatus.REJECTED ? (
        <Stack
          className="bg-neutral-white100 mt-2 rounded-sm border-l-4"
          style={styles.sideBorder}
        >
          <XStack className="py-4/2 px-3">
            <Stack className="bg-success-150 absolute left-[30] h-4 w-1" />
            <Stack className="bg-success-150 absolute left-3 top-3 items-center rounded-lg">
              <Icon
                type={'RupeeCircleOutlined'}
                size={20}
                color={zetColors.neutral.white100}
                className="m-1.5"
              />
            </Stack>
            <Stack className="left-6 mt-2 justify-center">
              <SH3 className="  text-grays-80">{i18n.t('payment.payment')}</SH3>
            </Stack>
          </XStack>
          {commissionLevels()}
          {totalCommission()}
          {payment?.concat(coins)?.map((payment) => payoutCards(payment))}
        </Stack>
      ) : null}
      {status === PaymentStatus.EXPIRED ? (
        <Stack
          className="bg-neutral-white100 mt-2 rounded-sm border-l-4"
          style={styles.expiredBorder}
        >
          <XStack className="py-4/2 px-3">
            <Stack className="bg-error-150 absolute left-[30] h-4 w-1" />
            <Stack className="bg-error-150 absolute left-3 top-3 items-center rounded-lg">
              <Icon
                type={'RupeeCircleOutlined'}
                size={20}
                color={zetColors.neutral.white100}
                className="m-1.5"
              />
            </Stack>
            <Stack className="left-6 mt-2 justify-center">
              <SH3 className="  text-grays-80">{i18n.t('payment.payment')}</SH3>
            </Stack>
          </XStack>
          <Stack className="mb-4/2 bg-error-20 mx-3 rounded">
            <YStack className="py-4/2 px-2">
              <B4 className="text-grays-80">
                {i18n.t('payment.expiredOn', {
                  date: moment.utc(payoutDate).format('DD-MMM-YYYY'),
                })}
              </B4>
              <B2 className="text-[#596379]">
                {i18n.t('payment.customerExpiredPaymentInfo', {
                  amount: payment?.[0]?.commission || 0,
                })}
              </B2>
            </YStack>
          </Stack>
        </Stack>
      ) : null}

      {status === PaymentStatus.LOCKED ? (
        <Stack
          className="bg-neutral-white100 mt-2 rounded-sm border-l-4"
          style={styles.sideBorder}
        >
          <XStack className="py-4/2 px-3">
            <Stack className="bg-success-150 absolute left-[30] h-4 w-1" />
            <Stack className="bg-success-150 absolute left-3 top-3 items-center rounded-lg">
              <Icon
                type={'RupeeCircleOutlined'}
                size={20}
                color={zetColors.neutral.white100}
                className="m-1.5"
              />
            </Stack>
            <Stack className="left-6 mt-2 justify-center">
              <SH3 className="  text-grays-80">{i18n.t('payment.payment')}</SH3>
            </Stack>
          </XStack>
          {commissionLevels()}
          {totalCommission()}
          {payment?.concat(coins)?.map((payment) => payoutCards(payment))}
        </Stack>
      ) : null}

      {!PaymentCardStatus.includes(status || '') &&
      (leadStatus === LeadStatus.PARTIALLY_FULLFILLED ||
        leadStatus === LeadStatus.FULLFILLED) ? (
        <Stack
          className="bg-neutral-white100 mt-2 rounded-sm border-l-4"
          style={styles.sideBorder}
        >
          <XStack className="py-4/2 px-3">
            <Stack className="bg-success-150 absolute left-[30] h-4 w-1" />
            <Stack className="bg-success-150 absolute left-3 top-3 items-center rounded-lg">
              <Icon
                type={'RupeeCircleOutlined'}
                size={20}
                color={zetColors.neutral.white100}
                className="m-1.5"
              />
            </Stack>
            <Stack className="left-6 mt-2 justify-center">
              <SH3 className="  text-grays-80">{i18n.t('payment.payment')}</SH3>
            </Stack>
          </XStack>
          <Stack className="mb-4/2 bg-error-10 mx-3 rounded">
            <YStack className="py-4/2 px-2">
              <B3 className="text-grays-70">
                {i18n.t('payment.customerBackInfo')}
              </B3>
            </YStack>
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  )
}

export default PaymentCard

const styles = StyleSheet.create({
  sideBorder: {
    borderColor: colors.success[50],
  },
  coinsBorder: {
    borderColor: colors.success[30],
  },
  failedErrorBorder: {
    borderColor: colors.error[20],
  },
  expiredBorder: {
    borderColor: colors.error[50],
  },
})
